import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FormNavigationGuard from './FormNavigationGuard';

export interface FormProtectionContextType {
  isFormDirty: boolean;
  setIsFormDirty: (dirty: boolean) => void;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  confirmNavigation: () => boolean;
}

const FormProtectionContext = createContext<FormProtectionContextType | undefined>(undefined);

export const useFormProtectionContext = () => {
  const context = useContext(FormProtectionContext);
  if (!context) {
    throw new Error('useFormProtectionContext는 FormProtectionProvider 내부에서 사용되어야 합니다.');
  }
  return context;
};

interface FormProtectionProviderProps {
  children: React.ReactNode;
}

const FormProtectionProvider: React.FC<FormProtectionProviderProps> = ({ children }) => {
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // 브라우저 이탈 시 경고 메시지
  const handleBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
    if (hasUnsavedChanges) {
      event.preventDefault();
      event.returnValue = '저장되지 않은 변경사항이 있습니다. 정말로 페이지를 떠나시겠습니까?';
      return event.returnValue;
    }
  }, [hasUnsavedChanges]);

  // 페이지 네비게이션 확인
  const confirmNavigation = useCallback((): boolean => {
    if (hasUnsavedChanges) {
      return window.confirm('저장되지 않은 변경사항이 있습니다. 정말로 페이지를 떠나시겠습니까?');
    }
    return true;
  }, [hasUnsavedChanges]);

  // 폼 dirty 상태가 변경될 때마다 unsaved changes 상태도 함께 업데이트
  const handleSetIsFormDirty = useCallback((dirty: boolean) => {
    setIsFormDirty(dirty);
    if (!dirty) {
      setHasUnsavedChanges(false);
    }
  }, []);

  // beforeunload 이벤트 리스너 등록/해제
  useEffect(() => {
    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, handleBeforeUnload]);

  const contextValue: FormProtectionContextType = {
    isFormDirty,
    setIsFormDirty: handleSetIsFormDirty,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    confirmNavigation,
  };

  return (
    <FormProtectionContext.Provider value={contextValue}>
      <FormNavigationGuard />
      {children}
    </FormProtectionContext.Provider>
  );
};

export default FormProtectionProvider; 